import { X } from "lucide-react";
import { Button } from "@babackapp/ui/components/button";
import { FieldIdDefinition, getFieldById } from "@/lib/override-rules";
import { FieldSelector } from "./field-selector";
import { OperatorSelector } from "./operator-selector";
import { ValueInput } from "./value-input";
import type { ConditionalRuleDto } from "@babackapp/private-api-client/types";
import {
  FormField,
  FormItem,
  FormMessage,
} from "@babackapp/ui/components/form";
import useTranslation from "@babackapp/translations/use-translation";
import { cn } from "@babackapp/ui/lib/utils";

interface ConditionRowProps {
  conditionIndex: number;
  condition: ConditionalRuleDto;
  onChange: (condition: ConditionalRuleDto) => void;
  onRemove: () => void;
  canRemove?: boolean;
  disabled?: boolean;
}

export function ConditionRow({
  conditionIndex,
  condition,
  onChange,
  onRemove,
  canRemove = true,
  disabled,
}: ConditionRowProps) {
  const t = useTranslation();
  const handleFieldChange = (fieldId: string) => {
    const newField = getFieldById(fieldId);
    if (newField) {
      onChange({
        ...condition,
        id: fieldId,
        type: newField.type,
        operator: undefined,
        value: "",
      } as unknown as ConditionalRuleDto);
    }
  };

  const handleOperatorChange = (operator: ConditionalRuleDto["operator"]) => {
    onChange({
      ...condition,
      operator,
      value: "",
    });
  };

  const handleValueChange = (
    value: string | number | boolean | string[] | number[],
  ) => {
    onChange({
      ...condition,
      value,
    });
  };

  const fullWidth =
    (condition.operator === "IN" || condition.operator === "NOT_IN") &&
    "w-full";

  return (
    <div
      className={cn(
        "self-stretch justify-between flex items-start gap-2 p-2 border rounded-md bg-white",
        fullWidth,
      )}
    >
      <div className={cn("flex", fullWidth)}>
        <div className="flex flex-1 flex-wrap items-start gap-2">
          <div className="flex items-start gap-2 flex-1 flex-wrap xxs:flex-nowrap">
            <FormField
              name={`conditions.rules.${conditionIndex}.id`}
              render={() => (
                <FormItem>
                  <FieldSelector
                    value={condition.id as FieldIdDefinition}
                    onChange={handleFieldChange}
                    disabled={disabled}
                    placeholder={t(
                      "overrideRules.conditionRow.fieldPlaceholder",
                    )}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`conditions.rules.${conditionIndex}.operator`}
              render={() => (
                <FormItem>
                  <OperatorSelector
                    value={condition.operator}
                    onChange={handleOperatorChange}
                    fieldId={condition.id}
                    disabled={disabled}
                    placeholder={t(
                      "overrideRules.conditionRow.operatorPlaceholder",
                    )}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn("flex items-center gap-2", fullWidth)}>
            <FormField
              name={`conditions.rules.${conditionIndex}.value`}
              render={() => (
                <FormItem className={cn(fullWidth)}>
                  <ValueInput
                    value={condition.value}
                    onChange={handleValueChange}
                    fieldId={condition.id}
                    operator={condition.operator}
                    disabled={disabled}
                  />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>

      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={disabled}
          className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
